import { ApplicationConfig, enableProdMode, importProvidersFrom, inject, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import {
  provideRouter,
  Router,
  withDisabledInitialNavigation,
  withEnabledBlockingInitialNavigation
} from '@angular/router';
import { routes } from './app.routes';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  BrowserUtils,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ApiAuthService } from '@core/services/api/api-auth.service';
import { tap } from 'rxjs';

import { RuntimeEnvironmentService } from '@core/services/runtime-environment.service';

// 🔹 Redirect unauthorized users
export function checkUserExists(apiAuthService: ApiAuthService, router: Router) {
  return () => apiAuthService.authorized().pipe(
    tap(async (exists) => {
      if (!exists) {
        await router.navigate(['/unauthorized']);
      }
    })
  );
}

// 🔹 Simple MSAL logger
export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

// 🔹 Ensure runtime config is loaded before MSAL factories run
export function initRuntimeFactory(env: RuntimeEnvironmentService) {
  return () => env.load();
}

// 🔹 MSAL Instance
export function MSALInstanceFactory(): IPublicClientApplication {
  const envService = inject(RuntimeEnvironmentService);
  const config = envService.msalConfig;

  return new PublicClientApplication({
    auth: config.auth,
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

// 🔹 MSAL Interceptor config
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const envService = inject(RuntimeEnvironmentService);
  const apiConfig = envService.apiConfig;

  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(`${apiConfig.uri}/*`, apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

// 🔹 MSAL Guard config
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  const envService = inject(RuntimeEnvironmentService);
  const apiConfig = envService.apiConfig;

  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...apiConfig.scopes]
    },
    loginFailedRoute: '/login-failed'
  };
}

// 🔹 Initial navigation mode
const initialNavigation = !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
  ? withEnabledBlockingInitialNavigation()
  : withDisabledInitialNavigation();

// 🔹 Application configuration
export const appConfig: ApplicationConfig = {
  providers: [
    DialogService,
    ConfirmationService,
    MessageService,
    importProvidersFrom(BrowserModule),
    provideRouter(routes, initialNavigation),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),

    // --- Load runtime config first ---
    { provide: APP_INITIALIZER, useFactory: initRuntimeFactory, deps: [RuntimeEnvironmentService], multi: true },

    // --- MSAL setup ---
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: LOCALE_ID,
      useValue: 'nl-NL',
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ]
};
